/**
 * blockSurface — the ONE place the chat render-blocks' surface chrome is
 * defined, so every block type reads the same border, radius, and padding and
 * a block sits flush with the app's own surfaces instead of inventing its own.
 *
 * Why this exists
 * ---------------
 * Chat blocks render INSIDE the app's answer card (`AIResponseBlock`,
 * `MessageRow`) and next to the app's entity cards (`Card`). Those canonical
 * surfaces are `rounded-2xl` (20px, `--radius` + 8px) with the softer
 * `border-border/70` hairline. A block drawn at `rounded-xl` (16px) with the
 * harder full-opacity `border-border` reads as a foreign element pasted into
 * the conversation — a smaller corner radius and a heavier border than the
 * card that contains it. Matching the app's surface token is what makes a block
 * feel native.
 *
 * The padding rule
 * ----------------
 * Internal padding must be at least the corner radius, or prose / code / a
 * figure visually collides with the rounded corner. At a 20px radius that means
 * a 20px (`p-5`) inset — the same inset the app's answer card uses (`p-4 sm:p-5`).
 * `p-4` (16px) is LESS than the radius and is exactly the "text crowds the
 * corner" cramping the live feedback flagged.
 *
 * Token-driven
 * ------------
 * Every value here is a token utility — `rounded-2xl` resolves to
 * `calc(var(--radius) + 8px)` and `border-border` to `hsl(var(--border))`, so
 * light/dark and per-studio retint flow through with no hardcoded color or
 * pixel radius. No motion or glow lives here; that stays the consumer's call.
 *
 * Usage
 * -----
 * A block whose whole body is one padded surface composes the full surface:
 *
 *   const statVariants = cva(cn(BLOCK_SURFACE, BLOCK_SURFACE_PADDING, "..."));
 *
 * A block with its own internal regions (a header bar + a body, like CodeBlock
 * or DataTable) takes the frame WITHOUT the blanket padding and pads each
 * region itself, reusing `BLOCK_SURFACE_INSET` so the region inset matches the
 * radius:
 *
 *   const codeBlockVariants = cva(cn(BLOCK_SURFACE_FRAME, "overflow-hidden"));
 */

/** The shared surface frame: radius + hairline border + card fill. */
export const BLOCK_SURFACE_FRAME = "rounded-2xl border border-border/70 bg-card text-card-foreground";

/**
 * The blanket internal padding for a single-region surface. Equals the corner
 * radius so content never crowds the curve. Mirrors the app answer card's
 * `p-4 sm:p-5` step-up on wider viewports.
 */
export const BLOCK_SURFACE_PADDING = "p-4 sm:p-5";

/** Frame + padding — the common case (a block that is one padded surface). */
export const BLOCK_SURFACE = `${BLOCK_SURFACE_FRAME} ${BLOCK_SURFACE_PADDING}`;

/**
 * The horizontal inset used by self-padding regions (a code/table header bar,
 * a chart caption) so their content lines up with the surface's rounded edge
 * at the same gutter the blanket padding uses.
 */
export const BLOCK_SURFACE_INSET = "px-4 sm:px-5";

/**
 * Vertical rhythm between stacked blocks (in `BlockList`) and between a
 * surface's internal rows. One notch calmer than the old `gap-3` so a run of
 * blocks reads as a relaxed column, matching the answer card's internal spacing.
 */
export const BLOCK_STACK_GAP = "gap-4";
